import React from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { upperFirst } from 'lodash';
import { format } from 'date-fns';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useChargeQueries from '@dashboard/hooks/queries/useChargeQueries';
import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import CardListItem from '@dashboard/components/CardListItem';
import EmptyState from '@dashboard/components/EmptyState';
import Spinner from '@dashboard/components/Spinner';
import TableRowElement from '@dashboard/components/TableRowElement';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import PaymentStatusBadge from '@/dashboard/components/PaymentItem/PaymentStatusBadge';
import isRecoveredAbandonment from '@/dashboard/services/abandonment/isRecoveredAbandonment';
import isAbandonedAbandonment from '@/dashboard/services/abandonment/isAbandonedAbandonment';
import Tooltip from '@/dashboard/components/Tooltip';
import { formatDateTime } from '@/dashboard/lib/format-date';
import StatusBadge from '@/dashboard/components/StatusBadge';
import Dash from '@/dashboard/components/Dash';

function PaymentsBookingsList({ sort, search, filter = {}, refreshKey, infinite = true }) {
  const { sellerId } = useAuth();

  const { getInfiniteCharges } = useChargeQueries();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: ['charges', sellerId, sort, search, filter, refreshKey],
    queryFn: async ({ pageParam }) =>
      await getInfiniteCharges(sellerId, {
        ...filter,
        search,
        limit: 10,
        skip: pageParam * 10,
        sort: JSON.stringify(sort),
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!infinite) return undefined;

      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
  });

  if (isLoading) {
    return <Spinner className="m-4" />;
  }

  if (isError) {
    return <div className="m-4">Unable to show bookings. Please refresh or contact support.</div>;
  }

  const isEmpty = !data?.pages?.[0]?.data?.length;

  if (isEmpty && search) {
    return (
      <EmptyState>
        <EmptyState.Title>No bookings found</EmptyState.Title>
        <EmptyState.Subtitle>There aren't any results for this filter.</EmptyState.Subtitle>
      </EmptyState>
    );
  }

  if (isEmpty) {
    if (filter.status) {
      return (
        <EmptyState>
          <EmptyState.Title>No bookings yet</EmptyState.Title>
          <EmptyState.Subtitle>
            {upperFirst(filter.status).replace(/_/g, ' ')} bookings will show up here.
          </EmptyState.Subtitle>
        </EmptyState>
      );
    }

    return (
      <EmptyState>
        <EmptyState.Title>No bookings yet</EmptyState.Title>
        <EmptyState.Subtitle>Your bookings will show up here.</EmptyState.Subtitle>
      </EmptyState>
    );
  }

  const hasFetchedAllPages = data?.pages?.[data?.pages?.length - 1]?.has_more === false;
  const calculateTotalTickets = tickets => {
    return tickets.reduce((total, ticket) => total + ticket.quantity, 0);
  };
  return (
    <>
      <BottomScrollListener onBottom={fetchNextPage}>
        {data?.pages?.map(data =>
          data?.data.map(payment => {
            return (
              <CardListItem key={payment._id} href={`/orders/bookings/${payment._id}`}>
                <TableRowElement size="fill" align="right">
                  <>
                    <div className="font-medium">
                      {payment.customerEmail || payment.customer?.email || <Dash />}
                    </div>
                  </>
                  <div className="font-thin text-gray-500">{payment.customer?.name ?? ''}</div>
                </TableRowElement>

                <TableRowElement size="xl">{payment.orderId}</TableRowElement>

                <TableRowElement size="xl" className="font-medium">
                  {getPriceLabel({
                    amount: payment.amount,
                    currency: payment.currency,
                  })}
                </TableRowElement>

                <TableRowElement size="large">
                  {calculateTotalTickets(payment.tickets)}
                </TableRowElement>

                <TableRowElement size="xl">
                  {format(new Date(payment.createdAt), 'd MMM, yyyy, HH:mm')}
                </TableRowElement>

                <TableRowElement size="large">
                  {(() => {
                    const isRecovered = isRecoveredAbandonment(payment);
                    const isAbandoned = isAbandonedAbandonment(payment);

                    if (isAbandoned) {
                      return <PaymentStatusBadge status="abandoned" livemode={payment.livemode} />;
                    }

                    if (isRecovered) {
                      return (
                        <Tooltip label={`Recovered on ${formatDateTime(payment.recoveredAt)}`}>
                          <StatusBadge status="recovered" label="Recovered" withInfoIcon />
                        </Tooltip>
                      );
                    }

                    return <>-</>;
                  })()}
                </TableRowElement>

                <TableRowElement size="medium" className="gap-1">
                  {(() => {
                    const isCanceledOrArchived =
                      payment.orderStatus === 'canceled' || payment.orderStatus === 'archived';
                    const isRefunded =
                      payment.status === 'refunded' || payment.status === 'partial_refund';

                    /** Show both badges when canceled + refunded */
                    if (payment.orderStatus === 'canceled' && isRefunded) {
                      return (
                        <>
                          <PaymentStatusBadge
                            status={payment.orderStatus}
                            livemode={payment.livemode}
                          />
                          <PaymentStatusBadge
                            status={payment.status}
                            livemode={payment.livemode}
                            error={payment.paymentError}
                          />
                        </>
                      );
                    }

                    if (isCanceledOrArchived && !isRefunded) {
                      return (
                        <PaymentStatusBadge
                          status={payment.orderStatus}
                          livemode={payment.livemode}
                        />
                      );
                    }

                    return (
                      <PaymentStatusBadge
                        status={payment.status}
                        livemode={payment.livemode}
                        error={payment.paymentError}
                      />
                    );
                  })()}
                </TableRowElement>
              </CardListItem>
            );
          }),
        )}
      </BottomScrollListener>

      {isFetchingNextPage && <Spinner className="m-4" />}

      {hasFetchedAllPages && (
        <div className="text-sm text-center text-gray-500 mt-2">No more bookings to show</div>
      )}
    </>
  );
}

export default PaymentsBookingsList;
